import * as ActionTypes from './actionTypes'
import taskPanelActionsGenerator from '../actionsGenerator'
import {ErrorsExceptions, Methods, ResultCodes, ValidationErrors} from '../constants'
import {getSingleCPSelection} from '../../../taskManagement/common/selectors'
import * as TaskManagementActions from '../../../taskManagement/common/actions'
import {addRecentlyCreatedTasks, setLastCreatedTaskIds} from '../../../taskManagement/common/actions'
import {Feature} from '../../../features/constants'
import * as FeaturesActions from '../../../features/actions'
import * as EntitiesActions from '../../../entities/actions'
import {addTask} from '../../../entities/actions'
import {getWorkflowStatus} from '../../../entities/selectors'
import {createAction} from 'redux-actions'
import messageResource from '../../../messageResource'
import {
    getAddedAssignedManagerIds,
    getAddedAssignedUserIds,
    getAssignedManagers,
    getAssignedUsers,
    getEstimatedTime,
    getTaskBillableTypeId,
    getTaskDeadline,
    getTaskDescription,
    getTaskName,
    getTaskStatusId,
    isTaskDataChanged
} from './selectors'
import {isBTYEnabled, isTWSEnabled} from '../../../features/selectors'
import * as SyncCallbacks from './syncCallbacks'
import assignmentsActionsGenerator from '../../usersAssignedToHierarchy/actionsGenerator'
import rpcCall from '../../../util/rpc/rpcCalls'
import {getLastTaskPanelTabKey} from '../editPanel/selectors'
import {FAKE_COMPLETED_WORKFLOW_STATUS_ID, FAKE_OPEN_WORKFLOW_STATUS_ID, WorkflowStatusType} from '../../../workflowStatus/constants'
import ExitConfirmationHandlersManager from '../../../ExitConfirmationHandlersManager'
import {arrayToSet} from '../../../atUtils'
import {isEnabled} from '../../../features/utils'
import {
    NO_RIGHT_TO_EDIT_HIERARCHY_EXCEPTION,
    NO_SUCH_PROJECT_EXCEPTION,
    PROJECT_IS_ARCHIVED_EXCEPTION,
    PROJECT_IS_NOT_AVAILABLE_EXCEPTION
} from '../../../taskManagement/common/ExceptionsTypes'
import {createTaskRpcErrorHandler} from '../utils'
import {Exceptions as CommonExceptions} from '../../../common/constants'
import {changeSingleCPSelection} from '../../../taskManagement/common/actions'
import {changeSingleCPSelectionAction} from '../../../taskManagement/common/actions'

const setAddedAssignedUserIds = createAction(ActionTypes.ADDED_ASSIGNED_USER_IDS_SET)
const setAddedAssignedManagerIds = createAction(ActionTypes.ADDED_ASSIGNED_MANAGER_IDS_SET)
const resetCreatePanelState = createAction(ActionTypes.CREATE_PANEL_STATE_RESET)
const setDefaultTaskDataProperty = createAction(ActionTypes.TASK_DATA_DEFAULT_STATE_PROPERTY_SET)

const taskPanelActions = taskPanelActionsGenerator({
    actionTypes: ActionTypes,
})

const assignmentsActions = assignmentsActionsGenerator({
    actionTypes: {
        ASSIGNED_USERS_SET: ActionTypes.ASSIGNED_USERS_SET,
        ASSIGNED_MANAGERS_SET: ActionTypes.ASSIGNED_MANAGERS_SET,
        CAN_MODIFY_ANY_USER_SET: ActionTypes.CAN_MODIFY_ANY_USER_SET,
        CAN_MODIFY_ANY_MANAGER_SET: ActionTypes.CAN_MODIFY_ANY_MANAGER_SET,
    },
})

export const deleteAssignedUser = (userId) => (dispatch) => {
    dispatch(updateAssignedUsers([], [userId]))
}

export const updateAssignedUsers = (addedUserIds, removedUserIds) => (dispatch, getState) => {
    const state = getState()
    const addedAssignedUserIds = {...getAddedAssignedUserIds(state)}
    addedUserIds.forEach(id => addedAssignedUserIds[id] = true)
    removedUserIds.forEach(id => delete addedAssignedUserIds[id])

    const users = setTaskAssignments(getAssignedUsers(state), addedAssignedUserIds)

    dispatch(setAddedAssignedUserIds(addedAssignedUserIds))
    dispatch(assignmentsActions.setAssignedUsers(users))
}

export const deleteAssignedManager = (managerData) => (dispatch) => {
    dispatch(updateAssignedManagers([], [managerData.id]))
}

export const updateAssignedManagers = (addedUserIds, removedUserIds) => (dispatch, getState) => {
    const state = getState()
    const addedAssignedManagerIds = {...getAddedAssignedManagerIds(state)}
    addedUserIds.forEach(id => addedAssignedManagerIds[id] = true)
    removedUserIds.forEach(id => delete addedAssignedManagerIds[id])

    const users = setTaskAssignments(getAssignedManagers(state), addedAssignedManagerIds)

    dispatch(setAddedAssignedManagerIds(addedAssignedManagerIds))
    dispatch(assignmentsActions.setAssignedManagers(users))
}

const setTaskAssignments = (users, addedIds) => {
    return users.map(user => {
        if (user.canBeModified) {
            return {...user, ...{hasTaskAssignment: !!addedIds[user.id]}}
        } else {
            return user
        }
    })
}

export const openCreateTaskPanel = () => (dispatch, getState) => {
    setUpExitConfirmation(getState)
    dispatch(resetCreatePanelState())
    dispatch(taskPanelActions.openTaskPanel())
}

export const closeCreateTaskPanel = () => (dispatch) => {
    ExitConfirmationHandlersManager.restoreExitConfirmation()
    dispatch(taskPanelActions.closeTaskPanel())
    dispatch(taskPanelActions.setTaskPanelLoaded(false))
    dispatch(taskPanelActions.setNeedInitializeFocus(false))
}

export const cancelTaskCreation = () => (dispatch, getState) => {
    const dataChanged = isTaskDataChanged(getState())

    if (!dataChanged || confirmCancelTaskCreation())
        dispatch(closeCreateTaskPanel())

    dispatch(taskPanelActions.setNeedInitializeFocus(false))
}

const confirmCancelTaskCreation = () => {
    return confirm(messageResource.getMessage('task.add.cancel_confirmation'))
}

const saveTask = createAction(ActionTypes.CREATE_TASK)
const saveTaskFailure = createAction(ActionTypes.CREATE_TASK_FAILURE)
const saveTaskSuccess = createAction(ActionTypes.CREATE_TASK_SUCCESS)

export const loadCreateTaskPanelData = () => (dispatch, getState) => {
    dispatch(taskPanelActions.setTaskPanelLoading(true))
    const selection = getSingleCPSelection(getState())
    rpcCall(Methods.LOAD_CREATE_TASK_PANEL, [selection.projectId, selection.customerId]).then(
        loadedData => {
            const enabledFeatures = arrayToSet(loadedData['enabledFeatures'])
            if (isEnabled(enabledFeatures, Feature.TWS)) {
                dispatch(EntitiesActions.setWorkflowStatusesList(loadedData['workflowStatuses']))
            }

            dispatch(FeaturesActions.setEnabledFeatures(enabledFeatures))

            const managementAssignments = loadedData['managementAssignments']
            const cannotManageProject = loadedData['cannotManageProject']
            TaskManagementActions.setManagementAssignments(managementAssignments)

            if (cannotManageProject) {
                alert(messageResource.getMessage("task.edit_popup.project_not_available"))
                dispatch(closeCreateTaskPanel())
                window.location.reload()
                return
            }

            if (isEnabled(enabledFeatures, Feature.BTY)) {
                dispatch(EntitiesActions.setTypesOfWorkList(loadedData['billingTypes']['list']))
                dispatch(taskPanelActions.setTaskInfo({typeOfWorkId: loadedData['defaultBillingTypeId']}))
                dispatch(setDefaultTaskDataProperty({typeOfWorkId: loadedData['defaultBillingTypeId']}))
            }

            dispatch(taskPanelActions.setTaskInfo({
                workflowStatusId: loadedData['firstStatusId'] || FAKE_OPEN_WORKFLOW_STATUS_ID,
            }))

            dispatch(setDefaultTaskDataProperty({
                workflowStatusId: loadedData['firstStatusId'] || FAKE_OPEN_WORKFLOW_STATUS_ID,
            }))

            if (isEnabled(enabledFeatures, Feature.WAS)) {
                const assignedUsersData = loadedData['assignedUsersData']
                dispatch(assignmentsActions.setAssignedUsersData({
                    ...assignedUsersData,
                    users: assignedUsersData.users.map(user =>
                        ({...user, canBeModified: !(user.hasAllAssignments || user.hasCustomerAssignment || user.hasProjectAssignment)})),
                }))
            }

            const assignedUsersDataForManagers = loadedData['assignedUsersDataForManagers']
            dispatch(assignmentsActions.setAssignedManagersData({
                ...assignedUsersDataForManagers,
                users: assignedUsersDataForManagers.users.map(user =>
                    ({...user, canBeModified: !(user.hasAllAssignments || user.hasCustomerAssignment || user.hasProjectAssignment)})),
            }))

            dispatch(setAddedAssignedUserIds({}))
            dispatch(setAddedAssignedManagerIds({}))
            dispatch(taskPanelActions.setTaskPanelLoading(false))
            dispatch(taskPanelActions.setTaskPanelLoaded(true))
            dispatch(taskPanelActions.setTaskPanelErrors([]))
            dispatch(taskPanelActions.setNeedInitializeFocus(true))
        }
    )
}

export const createTask = () => (dispatch, getState) => {
    dispatch(saveTask())
    const state = getState()
    const selection = getSingleCPSelection(state)
    const newTaskInfo = getNewTaskInfo(state, selection.projectId, selection.customerId)
    const addedAssignedUserIds = Object.keys(getAddedAssignedUserIds(state))
    const addedManagerIds = Object.keys(getAddedAssignedManagerIds(state))

    if (newTaskInfo.name.length === 0) {
        dispatch(taskPanelActions.addTaskPanelErrors([ValidationErrors.TASK_NAME_EMPTY]))
        return
    }

    rpcCall(Methods.CREATE_TASK, [newTaskInfo, addedAssignedUserIds, addedManagerIds],
        createTaskRpcErrorHandler,
    ).then(result => {
            const resultCode = result['resultCode']

            if (resultCode === ResultCodes.noRightsToManageProject) {
                alert(messageResource.getMessage("task.list.error.concurrently_unassigned_ma_project"))
                const managementAssignments = result['managementAssignments']
                TaskManagementActions.setManagementAssignments(managementAssignments)
                dispatch(saveTaskFailure())
                dispatch(closeCreateTaskPanel())
                return
            }

            if (resultCode === ResultCodes.billingTypeNotExists) {
                alert(messageResource.getMessage("task.edit_popup.billing_type_not_exists"))
                dispatch(saveTaskFailure())
                return
            }
            if (resultCode === ResultCodes.billingTypeIsArchived) {
                alert(messageResource.getMessage("task.edit_popup.billing_type_was_archived"))
                dispatch(saveTaskFailure())
                return
            }

            const enabledFeatures = arrayToSet(result['enabledFeatures'])

            if (isEnabled(enabledFeatures, Feature.TWS)) {
                dispatch(EntitiesActions.setWorkflowStatusesList(result['workflowStatuses']['list']))
            }

            dispatch(FeaturesActions.setEnabledFeatures(enabledFeatures))

            if (resultCode === ResultCodes.workflowStatusNotExists) {
                alert(messageResource.getMessage("change_tasks_workflow_status.no_such_workflow_status_error"))
                dispatch(saveTaskFailure())
                return
            }
            if (resultCode === ResultCodes.workflowStatusChanged) {
                alert(messageResource.getMessage("change_tasks_workflow_status.workflow_status_changed_error"))
                dispatch(saveTaskFailure())
                return
            }

            let newTask = {
                ...newTaskInfo,
                id: result['taskId'],
                typeOfWorkId: result['typeOfWorkId'],
                workflowStatusId: result['workflowStatusId'],
                lastTrackingDate: null,
                spentTime: 0,
                commentsNumber: 0,
                creationDate: Date.parseJSONDate(result["creationTimestamp"]),
            }
            delete newTask['workflowStatusName']

            dispatch(taskPanelActions.removeTaskPanelErrors([ValidationErrors.TASK_NAME_EMPTY, ValidationErrors.TASK_NAME_DUPLICATE]))
            dispatch(addTask(newTask))
            SyncCallbacks.afterTaskCreatedCb(newTask)
            EntitiesActions.addTask(newTask)
            dispatch(addRecentlyCreatedTasks([newTask.id]))
            dispatch(setLastCreatedTaskIds([newTask.id]))
            dispatch(saveTaskSuccess())
            dispatch(closeCreateTaskPanel())
        }).catch((error) => {
        switch (error.getException()) {
            case ErrorsExceptions.TASK_NAME_DUPLICATE:
                dispatch(taskPanelActions.removeTaskPanelErrors([ValidationErrors.TASK_NAME_EMPTY]))
                dispatch(taskPanelActions.addTaskPanelErrors([ValidationErrors.TASK_NAME_DUPLICATE]))
                break
            case NO_SUCH_PROJECT_EXCEPTION:
                alert(messageResource.getMessage("task.list.error.concurrently_delete_project"))
                window.location.reload()
                break
            case PROJECT_IS_ARCHIVED_EXCEPTION:
                alert(messageResource.getMessage("task.list.error.concurrently_archived_project"))
                window.location.reload()
                break
            case PROJECT_IS_NOT_AVAILABLE_EXCEPTION:
                alert(messageResource.getMessage("task.list.error.concurrently_unassigned_ma_project"))
                window.location.reload()
                break
            case NO_RIGHT_TO_EDIT_HIERARCHY_EXCEPTION:
                alert(messageResource.getMessage("task.create.error.no_manage_scope_right"))
                window.location.reload()
                return true
            case CommonExceptions.USER_IS_NOT_ASSIGNED_TO_MANAGER_EXCEPTION:
                alert(messageResource.getMessage("task.create.error.user_is_not_assigned_to_manager"))
                window.location.reload()
                break
            case CommonExceptions.USER_IS_DISABLED_EXCEPTION:
                alert(messageResource.getMessage("task.create.error.assigned_user_disabled"))
                window.location.reload()
                break
            case CommonExceptions.NO_SUCH_USER_EXCEPTION:
                alert(messageResource.getMessage("task.create.error.no_such_user"))
                window.location.reload()
                break
            case CommonExceptions.NO_RIGHTS_TO_SUBMIT_TT_EXCEPTION:
                alert(messageResource.getMessage("task.create.error.user_does_not_have_submit_tt_right"))
                window.location.reload()
                break
            case CommonExceptions.USER_HAS_NO_ACCESS_TO_SCOPE_EXCEPTION:
                alert(messageResource.getMessage("task.create.error.user_does_not_have_any_scope_rights"))
                window.location.reload()
                break
        }
    })
}

export const updateName = (name, prevName) => (dispatch) => {
    dispatch(taskPanelActions.setTaskInfo({name}))
    if (name && name !== prevName) {
        dispatch(taskPanelActions.removeTaskPanelErrors([ValidationErrors.TASK_NAME_EMPTY, ValidationErrors.TASK_NAME_DUPLICATE]))
    } else if (!name) {
        dispatch(taskPanelActions.addTaskPanelErrors([ValidationErrors.TASK_NAME_EMPTY]))
        dispatch(taskPanelActions.removeTaskPanelErrors([ValidationErrors.TASK_NAME_DUPLICATE]))
    }
}

export const updateStatus = (workflowStatusId) => (dispatch, getState) => {
    const state = getState()
    let completed = workflowStatusId === FAKE_COMPLETED_WORKFLOW_STATUS_ID
    if (isTWSEnabled(state)) {
        const workflowStatus = getWorkflowStatus(state, workflowStatusId)
        completed = workflowStatus.type === WorkflowStatusType.COMPLETED
    }
    dispatch(taskPanelActions.setTaskInfo({
        workflowStatusId,
        completed,
    }))

}

export const updateDeadline = (deadline) => (dispatch) => {
    dispatch(taskPanelActions.setTaskInfo({deadline}))
}

export const updateTypeOfWork = (typeOfWorkId) => (dispatch) => {
    dispatch(taskPanelActions.setTaskInfo({typeOfWorkId}))
}

export const updateEstimate = (estimatedTime) => (dispatch) => {
    dispatch(taskPanelActions.setTaskInfo({estimatedTime}))
}

export const updateDescription = (description) => (dispatch) => {
    dispatch(taskPanelActions.setTaskInfo({description}))
}

export const setTaskPanelTab = taskPanelActions.setTaskPanelTab

export const setLastTaskPanelTab = taskPanelActions.setLastTaskPanelTab

export const openLastTaskPanelTab = () => (dispatch, getState) => {
    const state = getState()
    const lastTaskPanelTabKey = getLastTaskPanelTabKey(state)
    dispatch(setTaskPanelTab(lastTaskPanelTabKey))
}

const getNewTaskInfo = (state, projectId, customerId) => {
    const name = getTaskName(state)

    let workflowStatusId = null
    let workflowStatusName = null
    let completed = getTaskStatusId(state) === FAKE_COMPLETED_WORKFLOW_STATUS_ID
    if (isTWSEnabled(state)) {
        workflowStatusId = getTaskStatusId(state)
        const workflowStatus = getWorkflowStatus(state, workflowStatusId)
        workflowStatusName = workflowStatus.name
        completed = workflowStatus.type === WorkflowStatusType.COMPLETED
    }

    const deadline = getTaskDeadline(state)

    let typeOfWorkId = null
    if (isBTYEnabled(state)) {
        typeOfWorkId = getTaskBillableTypeId(state)
    }

    const estimatedTime = getEstimatedTime(state)
    const description = getTaskDescription(state)

    return {
        projectId,
        customerId,
        name,
        workflowStatusId,
        workflowStatusName,
        completed,
        deadline,
        typeOfWorkId,
        estimatedTime,
        description,
    }
}

const setUpExitConfirmation = (getState) => {
    ExitConfirmationHandlersManager.setUpExitConfirmation(messageResource.getMessage('task.add.cancel_confirmation'), () => {
        return isTaskDataChanged(getState())
    }, false)
}


// WEBPACK FOOTER //
// ./web/js/panels/task/createPanel/actions.js